import { Component } from '@theme/component';

/**
 * Publica un evento personalizado para el píxel de analítica (GA4) sin romper la página si no existe.
 * @param {string} nombre
 * @param {Record<string, unknown>} [datos]
 */
function publicarEvento(nombre, datos = {}) {
  try {
    window.Shopify?.analytics?.publish?.(nombre, datos);
  } catch {
    // La analítica nunca debe interrumpir el formulario.
  }
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [resultado] - Confirmación o resumen de errores después de enviar.
 * @property {HTMLInputElement} [puntaje] - Campo oculto con el puntaje de la autoevaluación.
 * @property {HTMLElement} [avisoPuntaje] - Aviso visible de que el puntaje se incluirá.
 * @property {HTMLInputElement} [areas] - Campo oculto con las áreas por mejorar de la autoevaluación.
 * @property {HTMLInputElement} [calculo] - Campo oculto con el cálculo de margen de la calculadora.
 * @property {HTMLButtonElement} [botonCalendario] - Botón que muestra el calendario.
 * @property {HTMLElement} [calendario] - Contenedor del calendario embebido.
 */

/**
 * Formulario de agenda de Oscar Jaimes: precarga el puntaje de la autoevaluación, mueve el foco al
 * resultado del envío, publica generate_lead y carga el calendario solo cuando se pide.
 *
 * @extends {Component<Refs>}
 */
class OjContactoComponent extends Component {
  /** @type {AbortController | undefined} */
  #controlador;

  connectedCallback() {
    super.connectedCallback();

    this.#precargarPuntaje();

    const { resultado } = this.refs;
    if (!resultado) return;

    resultado.focus();

    if (resultado.dataset.estado === 'enviado') {
      publicarEvento('generate_lead', { formulario: 'agenda', pagina: window.location.pathname });
      this.#limpiarUrl();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controlador?.abort();
  }

  /**
   * Muestra el calendario (Calendly) y lo carga la primera vez.
   * @param {MouseEvent} _event
   */
  mostrarCalendario(_event) {
    const { calendario, botonCalendario } = this.refs;
    const enlace = this.dataset.calendario;
    if (!calendario || !enlace) return;

    if (!calendario.querySelector('iframe')) {
      let destino;
      try {
        destino = new URL(enlace);
      } catch {
        return;
      }
      destino.searchParams.set('embed_domain', window.location.hostname);
      destino.searchParams.set('embed_type', 'Inline');
      destino.searchParams.set('hide_gdpr_banner', '1');

      const iframe = document.createElement('iframe');
      iframe.src = destino.toString();
      iframe.title = 'Calendario para elegir el horario de la sesión de diagnóstico';
      calendario.append(iframe);

      this.#escucharReserva(destino.origin);
    }

    calendario.hidden = false;
    botonCalendario?.setAttribute('aria-expanded', 'true');
    publicarEvento('calendly_open', { pagina: window.location.pathname });
  }

  /**
   * Publica calendly_booked cuando el calendario confirma la cita.
   * @param {string} origen
   */
  #escucharReserva(origen) {
    this.#controlador?.abort();
    this.#controlador = new AbortController();

    window.addEventListener(
      'message',
      (event) => {
        if (event.origin !== origen) return;
        const datos = /** @type {{ event?: string } | null} */ (event.data);
        if (datos?.event === 'calendly.event_scheduled') {
          publicarEvento('calendly_booked', { pagina: window.location.pathname });
        }
      },
      { signal: this.#controlador.signal }
    );
  }

  #precargarPuntaje() {
    const { puntaje, areas, calculo, avisoPuntaje } = this.refs;
    const parametros = new URL(window.location.href).searchParams;
    const avisos = [];

    const valor = parametros.get('puntaje');
    if (puntaje && valor && /^\d{1,3}$/.test(valor) && Number(valor) <= 100) {
      puntaje.value = `${valor}/100`;
      const areasTexto = OjContactoComponent.#texto(parametros.get('areas'), 160);
      if (areas && areasTexto) areas.value = areasTexto;
      avisos.push(`tu puntaje de la autoevaluación (${valor} de 100)`);
      if (areasTexto) avisos.push(`tus áreas por mejorar (${areasTexto})`);
    }

    const calculoTexto = OjContactoComponent.#texto(parametros.get('calculo'), 320);
    if (calculo && calculoTexto) {
      calculo.value = calculoTexto;
      avisos.push('el cálculo de margen que hiciste en el sitio');
    }

    if (avisoPuntaje && avisos.length > 0) {
      const lista = avisos.length > 1 ? `${avisos.slice(0, -1).join(', ')} y ${avisos.at(-1)}` : avisos[0];
      avisoPuntaje.textContent = `Para preparar la sesión incluiré ${lista}.`;
      avisoPuntaje.hidden = false;
    }
  }

  /**
   * Texto de la URL limpio: sin caracteres de control, sin espacios repetidos y con un límite de largo.
   * @param {string | null} valor
   * @param {number} maximo
   */
  static #texto(valor, maximo) {
    if (!valor) return '';
    return valor
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maximo);
  }

  /** Quita contact_posted de la URL para que recargar no vuelva a contar el envío. */
  #limpiarUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('contact_posted')) return;
    url.searchParams.delete('contact_posted');
    window.history.replaceState(window.history.state, '', url);
  }
}

if (!customElements.get('oj-contacto-component')) {
  customElements.define('oj-contacto-component', OjContactoComponent);
}
